import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useRouteError,
} from 'react-router-dom';

import {
  Box,
  Button,
  Collapse,
  Container,
  Paper,
  Typography,
} from '@mui/material';

import { Home as HomeIcon } from '@mui/icons-material';

import {
  AuthProvider,
  Layout,
  ProtectedRoute,
  QueryClientProvider,
  URQLProvider,
  UserProfileProvider,
} from '@/components/common';

import {
  EmissionsDashboard,
  Heatmap as EventMap,
  Maintenance,
  ManageCo2e,
  ReeferInsights,
} from '@/components/pages';

const { VITE_APP_MAINTENANCE_MODE } = import.meta.env;

export const RouterPaths = {
  emissionsAndEnergy: '/emissions',
  manageCo2e: '/manage-co2e',
  eventMap: '/event-map',
  maintenance: '/maintenance',
  reeferInsights: '/reefer-insights',
};

// Composant de gestion d'erreur
export function CustomErrorBoundary() {
  const error = useRouteError() as Error;
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
      width="100%"
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="error">
            An error occurred
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We apologize for the inconvenience. Our team has been notified and
            is working on the issue.
          </Typography>

          {import.meta.env.DEV && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                sx={{ mb: 1 }}
              >
                {showDetails ? 'Hide' : 'Show'} technical details
              </Button>
              <Collapse in={showDetails}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    overflowX: 'auto',
                  }}
                >
                  <Typography variant="body2" component="pre" sx={{ m: 0 }}>
                    {error.message}
                  </Typography>
                </Paper>
              </Collapse>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => (window.location.href = '/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const Router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <URQLProvider>
          <QueryClientProvider>
            <UserProfileProvider>
              <ProtectedRoute component={Layout} />
            </UserProfileProvider>
          </QueryClientProvider>
        </URQLProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: '',
        element: <Outlet />,
        errorElement: <CustomErrorBoundary />,
        children: [
          {
            index: true,
            element: (
              <Navigate
                to={
                  VITE_APP_MAINTENANCE_MODE.toLowerCase() === 'true'
                    ? RouterPaths.maintenance
                    : RouterPaths.emissionsAndEnergy
                }
              />
            ),
          },
          {
            path: RouterPaths.emissionsAndEnergy,
            element: <EmissionsDashboard />,
          },
          {
            path: RouterPaths.manageCo2e,
            element: <ManageCo2e />,
          },
          {
            path: RouterPaths.eventMap,
            element: <EventMap />,
          },
          {
            path: RouterPaths.reeferInsights,
            element: <ReeferInsights />,
          },
        ],
      },
    ],
  },
  {
    path: RouterPaths.maintenance,
    element: <Maintenance />,
  },
]);

if (import.meta.hot) {
  import.meta.hot.dispose(() => Router.dispose());
}

export { Router };
